// src/hooks/useObfuscationGate.ts
// Демо-имитация «обфускации данных» перед обращением к ВНЕШНЕЙ языковой модели.
// При старте запроса (isLoading: false → true) проверяет активную модель: если она
// внешняя — на 7–10 c поднимает флаг obfuscating, в течение которого UI обязан скрывать
// ответ и показывать индикатор обфускации. Для внутренней модели гейт не активируется.
import { useEffect, useRef, useState } from 'react';
import { getActiveModel } from 'services/demoApi/modelsAdminApi';

const MIN_MS = 7000;
const MAX_MS = 10000;

export interface ObfuscationGate {
  /** true пока идёт имитация обфускации — ответ показывать нельзя */
  obfuscating: boolean;
  /** активная модель внешняя (обфускация применима) */
  external: boolean;
}

export function useObfuscationGate(isLoading: boolean): ObfuscationGate {
  const [obfuscating, setObfuscating] = useState(false);
  const [external, setExternal] = useState(false);
  const prevLoadingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;
    if (wasLoading || !isLoading) return;

    // Восходящий фронт isLoading — новый запрос к модели.
    let cancelled = false;
    setObfuscating(false);
    setExternal(false);
    if (timerRef.current) clearTimeout(timerRef.current);

    getActiveModel().then((model) => {
      if (cancelled || !model?.external) return;
      setExternal(true);
      setObfuscating(true);
      const duration = MIN_MS + Math.random() * (MAX_MS - MIN_MS);
      timerRef.current = setTimeout(() => setObfuscating(false), duration);
    });

    return () => {
      cancelled = true;
    };
  }, [isLoading]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return { obfuscating, external };
}
